import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import AdminShell, { Segmented, StatCard } from "@/components/admin/AdminShell";
import { api, getErrorMessage } from "@/lib/api";

const INTERVALS = ["15m", "1h", "4h", "1d"];
const SUB_TABS = [
  { id: "chart", label: "Chart & activity" },
  { id: "pairs", label: "Coin settings" },
  { id: "movers", label: "Movers" },
];

const pct = (n) => `${n >= 0 ? "+" : ""}${Number(n || 0).toFixed(2)}%`;
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

function fmtPrice(n) {
  const v = Number(n) || 0;
  if (!v) return "—";
  if (v >= 1) return v.toLocaleString("en-US", { maximumFractionDigits: v >= 100 ? 2 : 4 });
  return v.toPrecision(4);
}

function fmtCompact(n) {
  const v = Number(n) || 0;
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(v);
}

const quoteVol = (c) => Number(c?.quote_volume_24h) || 0;

function CoinIcon({ coin, size = "h-5 w-5" }) {
  const [broken, setBroken] = useState(false);
  if (coin?.image && !broken) {
    return <img src={coin.image} alt="" className={`${size} shrink-0 rounded-full`} onError={() => setBroken(true)} />;
  }
  return (
    <span className={`${size} grid shrink-0 place-items-center rounded-full bg-surface-2 text-[9px] font-semibold text-muted-foreground`}>
      {coin?.symbol?.slice(0, 1)}
    </span>
  );
}

function Toggle({ on, onChange, label, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={disabled ? `${label} is not supported for this coin` : label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      data-testid={testid}
      className={`relative h-5 w-9 rounded-full transition disabled:cursor-not-allowed disabled:opacity-40 ${on ? "bg-up" : "bg-surface-2 ring-1 ring-border"}`}
    >
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
    </button>
  );
}

function SearchBox({ value, onChange, placeholder }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-1.5">
      <Search className="h-3.5 w-3.5 text-muted-foreground" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full min-w-0 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
}

export default function AdminMarkets() {
  const [items, setItems] = useState([]);
  const [usdtInr, setUsdtInr] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [live, setLive] = useState(false);
  const [symbol, setSymbol] = useState("BTC");
  const [interval, setInterval_] = useState("1h");
  const [sub, setSub] = useState("chart");
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin/markets");
      setItems(data.items || []);
      setUsdtInr(data.usdt_inr || 0);
      setUpdatedAt(data.updated_at);
      setLive(true);
    } catch (err) {
      setLive(false);
      setError(getErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  async function patch(sym, p) {
    setError(null);
    setItems((prev) => prev.map((s) => (s.symbol === sym ? { ...s, ...p } : s)));
    try {
      await api.patch(`/api/admin/markets/${sym}`, p);
    } catch (err) {
      setError(getErrorMessage(err));
      load();
      throw err;
    }
  }

  const coin = items.find((a) => a.symbol === symbol) || items[0];
  const select = (sym) => { setSymbol(sym); setSub("chart"); };
  const notional = items.reduce((s, x) => s + (x.otc_notional_24h_inr || 0), 0);

  return (
    <AdminShell
      title="Markets"
      subtitle="Reference prices from Binance plus our P2P and OTC activity per coin. Coin settings control what the website shows and what the P2P and OTC desks accept."
      actions={
        <span className={`font-mono text-xs ${live ? "text-up" : "text-muted-foreground"}`} data-testid="markets-live">
          {live ? "● LIVE" : "○ CONNECTING"}
          {updatedAt && <span className="ml-2 text-muted-foreground">{new Date(updatedAt).toLocaleTimeString()}</span>}
        </span>
      }
    >
      <div className="space-y-4" data-testid="admin-markets">
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Listed on site" value={items.length ? `${items.filter((s) => s.listed).length} / ${items.length}` : "—"} />
          <StatCard label="P2P enabled" value={items.filter((s) => s.p2p_enabled).length} tone="up" />
          <StatCard label="OTC enabled" value={items.filter((s) => s.otc_enabled).length} tone="up" />
          <StatCard label="OTC notional 24h" value={inr(notional)} />
        </div>
        {error && <p className="text-sm text-down">{error}</p>}

        {coin && <TickerBar coin={coin} coins={items} usdtInr={usdtInr} onSelect={setSymbol} />}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Segmented items={SUB_TABS} value={sub} onChange={setSub} testIdPrefix="markets-tab" />
          {sub === "chart" && (
            <div className="flex gap-1 rounded-lg border border-border bg-surface-2 p-1 font-mono">
              {INTERVALS.map((iv) => (
                <button
                  key={iv}
                  type="button"
                  onClick={() => setInterval_(iv)}
                  className={`rounded-md px-2.5 py-1 text-xs ${interval === iv ? "bg-surface text-foreground ring-1 ring-border" : "text-muted-foreground"}`}
                >
                  {iv}
                </button>
              ))}
            </div>
          )}
        </div>

        {sub === "chart" && coin && (
          <div className="grid gap-4 xl:grid-cols-[230px_minmax(0,1fr)_290px]">
            <PairList coins={items} selected={coin.symbol} onSelect={setSymbol} />
            <CandleChart symbol={coin.symbol} interval={interval} livePrice={coin.price_usdt} />
            <PlatformActivity symbol={coin.symbol} />
          </div>
        )}
        {sub === "pairs" && <PairsTable coins={items} onPatch={patch} onOpen={select} />}
        {sub === "movers" && <Movers coins={items} onSelect={select} />}
        {!items.length && !error && <div className="skeleton h-64 rounded-xl" />}
      </div>
    </AdminShell>
  );
}

function TickerBar({ coin, coins, usdtInr, onSelect }) {
  const up = (coin.change_pct_24h || 0) >= 0;
  const stats = [
    { label: "24h change", value: pct(coin.change_pct_24h), tone: up ? "text-up" : "text-down" },
    { label: "High / low", value: `${fmtPrice(coin.high_24h)} / ${fmtPrice(coin.low_24h)}` },
    { label: "Volume", value: `$${fmtCompact(quoteVol(coin))}` },
    { label: "INR price", value: coin.price_inr ? inr(coin.price_inr) : "—" },
    { label: "USDT / INR", value: usdtInr ? `₹${usdtInr.toFixed(2)}` : "—" },
    {
      label: "Live P2P ads",
      value: coin.p2p_supported ? String(coin.live_ads) : "Not offered",
      tone: coin.p2p_supported && !coin.p2p_enabled ? "text-down" : undefined,
    },
  ];
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-surface/50 px-4 py-3 xl:flex-row xl:items-center" data-testid="markets-ticker">
      <div className="flex shrink-0 items-center gap-3 xl:border-r xl:border-border xl:pr-6">
        <CoinIcon coin={coin} size="h-9 w-9" />
        <div className="min-w-0">
          <div className="flex items-baseline gap-3">
            <select
              value={coin.symbol}
              onChange={(e) => onSelect(e.target.value)}
              className="bg-transparent text-base font-semibold outline-none"
              aria-label="Select pair"
            >
              {coins.map((a) => (
                <option key={a.symbol} value={a.symbol} className="bg-surface">{a.symbol}/USDT</option>
              ))}
            </select>
            <span className={`num font-mono text-2xl font-semibold ${up ? "text-up" : "text-down"}`}>${fmtPrice(coin.price_usdt)}</span>
          </div>
          <p className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
            <span>{coin.name} · reference price, Binance</span>
            {coin.p2p_supported && !coin.p2p_enabled && <span className="text-down">P2P paused</span>}
            {coin.otc_supported && !coin.otc_enabled && <span className="text-down">OTC paused</span>}
            {!coin.listed && <span className="text-warning">Hidden on site</span>}
          </p>
        </div>
      </div>
      <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-6">
        {stats.map((s) => (
          <div key={s.label} className="min-w-0">
            <p className="truncate text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{s.label}</p>
            <p className={`num truncate font-mono text-sm ${s.tone || ""}`}>{s.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PairList({ coins, selected, onSelect }) {
  const [q, setQ] = useState("");
  const rows = coins.filter((a) => `${a.symbol} ${a.name}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="flex max-h-[420px] min-h-0 flex-col rounded-xl border border-border bg-surface/50 xl:h-0 xl:max-h-none xl:min-h-full">
      <div className="border-b border-border p-2">
        <SearchBox value={q} onChange={setQ} placeholder="Search pair" />
      </div>
      <div className="flex px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        <span className="flex-1">Pair</span>
        <span className="w-20 text-right">Price</span>
        <span className="w-14 text-right">24h</span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {rows.map((a) => {
          const paused = (a.p2p_supported && !a.p2p_enabled) || (a.otc_supported && !a.otc_enabled);
          return (
            <button
              key={a.symbol}
              type="button"
              onClick={() => onSelect(a.symbol)}
              className={`flex w-full items-center px-3 py-2 text-left font-mono text-xs transition hover:bg-surface-2/60 ${
                a.symbol === selected ? "bg-surface-2 ring-1 ring-inset ring-primary/40" : ""
              }`}
            >
              <span className="flex min-w-0 flex-1 items-center gap-1.5">
                <span className="truncate">{a.symbol}</span>
                {a.featured && <span className="text-warning">★</span>}
                {paused && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-down" title="P2P or OTC paused" />}
                {!a.listed && <span className="text-[9px] text-muted-foreground">HIDDEN</span>}
              </span>
              <span className="num w-20 text-right">{fmtPrice(a.price_usdt)}</span>
              <span className={`num w-14 text-right ${(a.change_pct_24h || 0) >= 0 ? "text-up" : "text-down"}`}>{pct(a.change_pct_24h)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const CHART_W = 800;
const PRICE_H = 280;
const VOL_TOP = 292;
const VOL_H = 56;
const AXIS_W = 64;

function CandleChart({ symbol, interval, livePrice }) {
  const [candles, setCandles] = useState([]);
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCandles([]);
    async function pull() {
      try {
        const { data } = await api.get(`/api/admin/markets/${symbol}/klines`, { params: { interval, limit: 96 } });
        if (cancelled) return;
        setCandles(data.candles || []);
        setNote(data.note || null);
      } catch (err) {
        if (!cancelled) setNote(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    pull();
    const id = setInterval(pull, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [symbol, interval]);

  const view = useMemo(() => {
    if (!candles.length) return null;
    const series = candles.map((c, i) =>
      i === candles.length - 1 && livePrice > 0 ? { ...c, c: livePrice, h: Math.max(c.h, livePrice), l: Math.min(c.l, livePrice) } : c,
    );
    const hi = Math.max(...series.map((c) => c.h));
    const lo = Math.min(...series.map((c) => c.l));
    const pad = (hi - lo) * 0.06 || hi * 0.001;
    const max = hi + pad;
    const min = lo - pad;
    const vmax = Math.max(...series.map((c) => c.v), 1);
    const plotW = CHART_W - AXIS_W;
    const step = plotW / series.length;
    const y = (p) => ((max - p) / (max - min)) * PRICE_H;
    const ticks = Array.from({ length: 5 }, (_, i) => min + ((max - min) * (i + 0.5)) / 5);
    return { series, step, y, vmax, ticks, plotW };
  }, [candles, livePrice]);

  function onMove(e) {
    if (!view || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * CHART_W;
    const idx = Math.floor(x / view.step);
    setHover(idx >= 0 && idx < view.series.length ? idx : null);
  }

  const active = view ? view.series[hover ?? view.series.length - 1] : null;
  const last = view?.series[view.series.length - 1];

  return (
    <div className="min-h-[400px] min-w-0 rounded-xl border border-border bg-surface/50 p-3" data-testid="markets-chart">
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px]">
        <span className="font-semibold">{symbol}/USDT · {interval}</span>
        {active && (
          <>
            <span className="text-muted-foreground">{new Date(active.t).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>
            {["o", "h", "l", "c"].map((k) => (
              <span key={k}>
                <span className="text-muted-foreground">{k.toUpperCase()} </span>
                <span className={active.c >= active.o ? "text-up" : "text-down"}>{fmtPrice(active[k])}</span>
              </span>
            ))}
            <span><span className="text-muted-foreground">V </span>{fmtCompact(active.v)}</span>
          </>
        )}
      </div>
      {!view ? (
        <p className="py-24 text-center text-sm text-muted-foreground">{loading ? "Loading candles…" : note || "No data"}</p>
      ) : (
        <svg
          ref={svgRef}
          viewBox={`0 0 ${CHART_W} ${VOL_TOP + VOL_H}`}
          className="h-auto w-full select-none"
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          {view.ticks.map((t) => (
            <g key={t}>
              <line x1={0} x2={view.plotW} y1={view.y(t)} y2={view.y(t)} className="stroke-border" strokeDasharray="2 4" strokeWidth={0.6} />
              <text x={CHART_W - 4} y={view.y(t) + 3} textAnchor="end" className="fill-muted-foreground font-mono" fontSize={10}>
                {fmtPrice(t)}
              </text>
            </g>
          ))}
          {view.series.map((c, i) => {
            const up = c.c >= c.o;
            const cx = i * view.step + view.step / 2;
            const bodyTop = view.y(Math.max(c.o, c.c));
            const bodyH = Math.max(1, Math.abs(view.y(c.o) - view.y(c.c)));
            const w = Math.max(1, view.step * 0.62);
            const vh = (c.v / view.vmax) * VOL_H;
            return (
              <g key={c.t} className={up ? "fill-up stroke-up" : "fill-down stroke-down"}>
                <line x1={cx} x2={cx} y1={view.y(c.h)} y2={view.y(c.l)} strokeWidth={1} />
                <rect x={cx - w / 2} y={bodyTop} width={w} height={bodyH} />
                <rect x={cx - w / 2} y={VOL_TOP + VOL_H - vh} width={w} height={vh} opacity={0.35} stroke="none" />
              </g>
            );
          })}
          {last && (
            <g>
              <line x1={0} x2={view.plotW} y1={view.y(last.c)} y2={view.y(last.c)} className={last.c >= last.o ? "stroke-up" : "stroke-down"} strokeDasharray="4 3" strokeWidth={0.8} />
              <rect x={view.plotW + 2} y={view.y(last.c) - 8} width={AXIS_W - 4} height={16} rx={3} className={last.c >= last.o ? "fill-up" : "fill-down"} />
              <text x={CHART_W - 6} y={view.y(last.c) + 3.5} textAnchor="end" fontSize={10} className="fill-background font-mono font-semibold">
                {fmtPrice(last.c)}
              </text>
            </g>
          )}
          {hover !== null && (
            <line
              x1={hover * view.step + view.step / 2}
              x2={hover * view.step + view.step / 2}
              y1={0}
              y2={VOL_TOP + VOL_H}
              className="stroke-muted-foreground"
              strokeWidth={0.6}
              strokeDasharray="3 3"
            />
          )}
          <line x1={0} x2={view.plotW} y1={VOL_TOP - 4} y2={VOL_TOP - 4} className="stroke-border" strokeWidth={0.6} />
        </svg>
      )}
    </div>
  );
}

function PlatformActivity({ symbol }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("p2p");

  useEffect(() => {
    let cancelled = false;
    setData(null);
    async function pull() {
      try {
        const { data: d } = await api.get(`/api/admin/markets/${symbol}/activity`);
        if (!cancelled) setData(d);
      } catch {
        /* keep last snapshot */
      }
    }
    pull();
    const id = setInterval(pull, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [symbol]);

  const stat = (label, value, tone) => (
    <div className="rounded-lg bg-surface-2/40 px-2.5 py-2">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`num mt-0.5 font-mono text-xs font-semibold ${tone || ""}`}>{value}</p>
    </div>
  );
  const head = (cols) => (
    <div className="flex px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
      <span className="flex-1">{cols[0]}</span>
      <span className="w-20 text-right">{cols[1]}</span>
      <span className="w-20 text-right">{cols[2]}</span>
    </div>
  );

  return (
    <div className="flex max-h-[420px] min-h-0 flex-col rounded-xl border border-border bg-surface/50 xl:h-0 xl:max-h-none xl:min-h-full" data-testid="markets-activity">
      <div className="flex gap-1 border-b border-border p-2">
        {["p2p", "otc"].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            className={`flex-1 rounded-md py-1 text-xs ${view === v ? "bg-surface-2 text-foreground" : "text-muted-foreground"}`}
          >
            {v === "p2p" ? "P2P book" : "OTC desk"}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto font-mono text-[11px]">
        {!data ? (
          <p className="p-6 text-center text-muted-foreground">Loading…</p>
        ) : view === "p2p" ? (
          !data.p2p.supported ? (
            <p className="p-6 text-center font-sans text-muted-foreground">P2P trades USDT only. {symbol} is not offered on the P2P book.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-1.5 p-2">
                {stat("Best buy ad", data.p2p.best_buy ? inr(data.p2p.best_buy) : "—", "text-up")}
                {stat("Best sell ad", data.p2p.best_sell ? inr(data.p2p.best_sell) : "—", "text-down")}
                {stat("Orders 24h", `${data.p2p.orders_24h} · ${data.p2p.open_orders} open`)}
                {stat("Volume 24h", inr(data.p2p.volume_24h_inr))}
                {stat("Live ads", `${data.p2p.buy_ads} buy · ${data.p2p.sell_ads} sell`)}
                {stat("Disputed", String(data.p2p.disputed), data.p2p.disputed ? "text-warning" : undefined)}
              </div>
              {head(["Merchant", "Price", "Available"])}
              {data.p2p.ads.length === 0 ? (
                <p className="px-3 py-4 text-center text-muted-foreground">No live ads for {symbol}</p>
              ) : (
                data.p2p.ads.map((ad) => (
                  <div key={ad.id} className="flex px-3 py-[3px]" title={`Limits ${inr(ad.min_limit)} – ${inr(ad.max_limit)}`}>
                    <span className="flex-1 truncate font-sans">
                      <span className={ad.side === "buy" ? "text-up" : "text-down"}>{ad.side === "buy" ? "B" : "S"}</span> {ad.merchant}
                    </span>
                    <span className="w-20 text-right">{ad.price ? inr(ad.price) : ad.price_type || "—"}</span>
                    <span className="w-20 text-right text-muted-foreground">{fmtCompact(ad.available)}</span>
                  </div>
                ))
              )}
            </>
          )
        ) : !data.otc.supported ? (
          <p className="p-6 text-center font-sans text-muted-foreground">The OTC desk quotes BTC and USDT only. {symbol} is not offered.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-1.5 p-2">
              {stat("Trades 24h", String(data.otc.trades_24h))}
              {stat("Notional 24h", inr(data.otc.notional_24h_inr))}
              {stat("Open requests", String(data.otc.open_requests), data.otc.open_requests ? "text-warning" : undefined)}
            </div>
            {head(["Client", "Rate", "Notional"])}
            {data.otc.recent.length === 0 ? (
              <p className="px-3 py-4 text-center text-muted-foreground">No OTC trades for {symbol} yet</p>
            ) : (
              data.otc.recent.map((t) => (
                <div key={t.id} className="flex px-3 py-[3px]" title={`${t.id} · ${t.date ? new Date(t.date).toLocaleString() : ""}`}>
                  <span className="flex-1 truncate font-sans">
                    <span className={t.side === "buy" ? "text-up" : "text-down"}>{t.side === "buy" ? "B" : "S"}</span> {t.user}
                  </span>
                  <span className="w-20 text-right">{inr(t.rate_inr)}</span>
                  <span className="w-20 text-right text-muted-foreground">{inr(t.total_inr)}</span>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PairsTable({ coins, onPatch, onOpen }) {
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const rows = coins.filter((s) => `${s.symbol} ${s.name}`.toLowerCase().includes(q.toLowerCase()));
  const save = (sym, p) => onPatch(sym, p).catch(() => undefined);

  return (
    <div className="space-y-3" data-testid="markets-pairs">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full sm:w-64"><SearchBox value={q} onChange={setQ} placeholder="Search pair" /></div>
        <span className="text-xs text-muted-foreground">
          <b className="text-foreground">Listed</b> shows the coin on the website Markets and home pages · <b className="text-foreground">Featured</b> pins it first ·{" "}
          <b className="text-foreground">P2P</b> / <b className="text-foreground">OTC</b> pause new orders and quotes for the coin.
        </span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border bg-surface/40">
        <table className="w-full text-xs">
          <thead className="bg-surface-2/50 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2.5 font-medium">Pair</th>
              <th className="px-3 py-2.5 text-right font-medium">Price</th>
              <th className="px-3 py-2.5 text-right font-medium">24h</th>
              <th className="px-3 py-2.5 text-right font-medium">Volume 24h</th>
              <th className="px-3 py-2.5 text-center font-medium">Listed</th>
              <th className="px-3 py-2.5 text-center font-medium">Featured</th>
              <th className="px-3 py-2.5 text-center font-medium">P2P</th>
              <th className="px-3 py-2.5 text-right font-medium">Live ads</th>
              <th className="px-3 py-2.5 text-center font-medium">OTC</th>
              <th className="px-3 py-2.5 text-right font-medium">OTC 24h</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => {
              const up = (s.change_pct_24h || 0) >= 0;
              return (
                <tr key={s.symbol} className="border-t border-border/60 hover:bg-surface-2/30" data-testid={`pair-row-${s.symbol}`}>
                  <td className="px-3 py-2">
                    <button type="button" className="flex items-center gap-2 text-left hover:text-primary" onClick={() => onOpen(s.symbol)}>
                      <CoinIcon coin={s} />
                      <span>
                        <span className="block font-mono font-semibold">{s.symbol}/USDT</span>
                        <span className="block text-[10px] text-muted-foreground">{s.name}</span>
                      </span>
                    </button>
                  </td>
                  <td className="num px-3 py-2 text-right font-mono">{fmtPrice(s.price_usdt)}</td>
                  <td className={`num px-3 py-2 text-right font-mono ${up ? "text-up" : "text-down"}`}>{pct(s.change_pct_24h)}</td>
                  <td className="num px-3 py-2 text-right font-mono text-muted-foreground">${fmtCompact(quoteVol(s))}</td>
                  <td className="px-3 py-2 text-center">
                    <Toggle on={s.listed} label="Listed" testid={`toggle-listed-${s.symbol}`} onChange={(v) => save(s.symbol, { listed: v })} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Toggle on={s.featured} label="Featured" testid={`toggle-featured-${s.symbol}`} onChange={(v) => save(s.symbol, { featured: v })} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Toggle
                      on={s.p2p_enabled}
                      label="P2P"
                      disabled={!s.p2p_supported}
                      testid={`toggle-p2p-${s.symbol}`}
                      onChange={(v) => {
                        if (!v && !window.confirm(`Pause P2P for ${s.symbol}? New ads and orders are rejected; open orders can still complete.`)) return;
                        save(s.symbol, { p2p_enabled: v });
                      }}
                    />
                  </td>
                  <td className="num px-3 py-2 text-right font-mono">{s.p2p_supported ? s.live_ads : "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <Toggle
                      on={s.otc_enabled}
                      label="OTC"
                      disabled={!s.otc_supported}
                      testid={`toggle-otc-${s.symbol}`}
                      onChange={(v) => {
                        if (!v && !window.confirm(`Pause the OTC desk for ${s.symbol}? New quote requests are rejected.`)) return;
                        save(s.symbol, { otc_enabled: v });
                      }}
                    />
                  </td>
                  <td className="num px-3 py-2 text-right font-mono">
                    {s.otc_trades_24h ? `${s.otc_trades_24h} · ${inr(s.otc_notional_24h_inr)}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" className="rounded-md border border-border px-2 py-1 text-xs hover:bg-surface-2" onClick={() => setEdit(s)}>
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-12 text-center text-muted-foreground">No pairs match “{q}”.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {edit && <PairEditor coin={edit} onClose={() => setEdit(null)} onSave={(p) => onPatch(edit.symbol, p).then(() => setEdit(null))} />}
    </div>
  );
}

function PairEditor({ coin, onClose, onSave }) {
  const [form, setForm] = useState({
    listed: !!coin.listed,
    featured: !!coin.featured,
    p2p_enabled: !!coin.p2p_enabled,
    otc_enabled: !!coin.otc_enabled,
    note: coin.note || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const row = (key, label, hint, disabled) => (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-surface-2/40 px-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <Toggle on={form[key]} label={label} disabled={disabled} onChange={(v) => setForm((f) => ({ ...f, [key]: v }))} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="pair-editor">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-md flex-col border-l border-border bg-surface shadow-[var(--shadow-panel)]">
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex items-center gap-3">
            <CoinIcon coin={coin} size="h-8 w-8" />
            <div>
              <h2 className="text-lg font-semibold">{coin.symbol} settings</h2>
              <p className="text-xs text-muted-foreground">Website visibility and desk availability</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-surface-2 hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form
          className="flex-1 space-y-3 overflow-y-auto p-5"
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            const p = {};
            for (const k of Object.keys(form)) if (form[k] !== (k === "note" ? coin.note || "" : !!coin[k])) p[k] = form[k];
            if (!Object.keys(p).length) { onClose(); return; }
            onSave(p)
              .catch((err) => setError(getErrorMessage(err)))
              .finally(() => setBusy(false));
          }}
        >
          {row("listed", "Listed on website", "Show on the Markets and home pages")}
          {row("featured", "Featured", "Pin first on the Markets list")}
          {row("p2p_enabled", "P2P trading", coin.p2p_supported ? "Accept new P2P ads and orders" : "P2P trades USDT only", !coin.p2p_supported)}
          {row("otc_enabled", "OTC desk", coin.otc_supported ? "Accept new OTC quote requests" : "OTC quotes BTC and USDT only", !coin.otc_supported)}
          <label className="block space-y-1">
            <span className="block text-xs text-muted-foreground">Internal note</span>
            <textarea
              maxLength={300}
              className="h-20 w-full resize-none rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs outline-none focus:border-primary"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            />
          </label>
          {error && <p className="text-xs text-down">{error}</p>}
          <button disabled={busy} className="brand-gradient w-full rounded-lg py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-40" data-testid="pair-save">
            {busy ? "Saving…" : "Save settings"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Movers({ coins, onSelect }) {
  const priced = coins.filter((a) => a.price_usdt > 0);
  const gainers = [...priced].sort((a, b) => b.change_pct_24h - a.change_pct_24h).slice(0, 5);
  const losers = [...priced].sort((a, b) => a.change_pct_24h - b.change_pct_24h).slice(0, 5);
  const volume = [...priced].sort((a, b) => quoteVol(b) - quoteVol(a)).slice(0, 5);
  const maxAbs = Math.max(1, ...priced.map((a) => Math.abs(a.change_pct_24h || 0)));

  const list = (title, rows, metric, tone) => (
    <div className="rounded-xl border border-border bg-surface/50 p-4">
      <p className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">{title}</p>
      <div className="space-y-1 font-mono text-xs">
        {rows.map((a, i) => (
          <button key={a.symbol} type="button" onClick={() => onSelect(a.symbol)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-surface-2/60">
            <span className="w-4 text-muted-foreground">{i + 1}</span>
            <span className="flex-1 font-semibold">{a.symbol}</span>
            <span className="num text-muted-foreground">${fmtPrice(a.price_usdt)}</span>
            <span className={`num w-20 text-right ${tone(a)}`}>{metric(a)}</span>
          </button>
        ))}
      </div>
    </div>
  );
  const changeTone = (a) => (a.change_pct_24h >= 0 ? "text-up" : "text-down");

  return (
    <div className="space-y-4" data-testid="markets-movers">
      <div className="grid gap-4 lg:grid-cols-3">
        {list("Top gainers 24h", gainers, (a) => pct(a.change_pct_24h), changeTone)}
        {list("Top losers 24h", losers, (a) => pct(a.change_pct_24h), changeTone)}
        {list("Volume leaders", volume, (a) => `$${fmtCompact(quoteVol(a))}`, () => "")}
      </div>
      <div className="rounded-xl border border-border bg-surface/50 p-4">
        <p className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">24h heatmap</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {[...priced].sort((a, b) => quoteVol(b) - quoteVol(a)).slice(0, 30).map((a) => {
            const strength = 0.12 + (Math.abs(a.change_pct_24h || 0) / maxAbs) * 0.5;
            return (
              <button
                key={a.symbol}
                type="button"
                onClick={() => onSelect(a.symbol)}
                className="rounded-lg border border-border p-3 text-left transition hover:ring-1 hover:ring-primary/50"
                style={{ background: `oklch(var(--${a.change_pct_24h >= 0 ? "up" : "down"}) / ${strength.toFixed(2)})` }}
              >
                <p className="font-mono text-sm font-semibold">{a.symbol}</p>
                <p className="num font-mono text-xs">{pct(a.change_pct_24h)}</p>
                <p className="num mt-1 font-mono text-[10px] text-muted-foreground">${fmtCompact(quoteVol(a))} vol</p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
